import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, catchError, firstValueFrom, of, switchMap, timer, timeout } from 'rxjs';
import Swal from 'sweetalert2';

import { environment } from '../../../../environments/environment';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth-refresh-context';
import { AuthService } from '../auth/authservice';
import { SessionRefreshService } from '../auth/session-refresh.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityTimeoutService {
  private readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly sessionRefresh = inject(SessionRefreshService);
  private readonly zone = inject(NgZone);
  private readonly warningAfterMs = 13 * 60 * 1000;
  private readonly timeoutWarningMs = 2 * 60 * 1000;
  private readonly activityThrottleMs = 1000;
  private readonly touchIntervalMs = 3 * 60 * 1000;
  private readonly accessTokenRefreshIntervalMs = 12 * 60 * 1000;
  private readonly cookieSettleDelayMs = 750;
  private readonly sessionVerifyRetryMs = 2500;
  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'];

  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private touchTimer: ReturnType<typeof setInterval> | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private sessionVerifyRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastActivityAt = Date.now();
  private started = false;
  private sessionWorkStarted = false;
  private warningOpen = false;

  start(): void {
    if (this.started || typeof window === 'undefined' || this.isLoginRoute()) {
      if (this.started && !this.sessionWorkStarted && typeof window !== 'undefined' && !this.isLoginRoute()) {
        this.verifyAndStartSessionWork();
      }
      return;
    }

    this.started = true;
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach((eventName) => {
        window.addEventListener(eventName, this.handleActivity, { passive: true });
      });
      this.verifyAndStartSessionWork();
    });
  }

  stop(): void {
    if (typeof window !== 'undefined') {
      this.activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, this.handleActivity);
      });
    }

    this.started = false;
    this.sessionWorkStarted = false;
    this.clearTimers();
    this.warningOpen = false;
    void Swal.close();
  }

  private readonly handleActivity = (): void => {
    if (this.warningOpen) {
      return;
    }

    const now = Date.now();
    if (now - this.lastActivityAt < this.activityThrottleMs) {
      return;
    }

    this.lastActivityAt = now;
    this.scheduleWarning();
  };

  private startSessionWork(): void {
    if (this.sessionWorkStarted || !this.started || this.isLoginRoute()) {
      return;
    }

    this.sessionWorkStarted = true;
    this.clearSessionVerifyRetryTimer();
    this.scheduleWarning();
    this.touchSession();
    this.touchTimer = setInterval(() => this.touchSession(), this.touchIntervalMs);
    this.refreshTimer = setInterval(() => {
      this.sessionRefresh.refresh().subscribe({
        next: () => undefined,
        error: () => undefined,
      });
    }, this.accessTokenRefreshIntervalMs);
  }

  private scheduleWarning(): void {
    this.clearWarningTimer();
    this.warningTimer = setTimeout(() => {
      void this.zone.run(() => this.showWarning());
    }, this.warningAfterMs);
  }

  private async showWarning(): Promise<void> {
    if (this.warningOpen || this.isLoginRoute()) {
      this.scheduleWarning();
      return;
    }

    this.warningOpen = true;
    const timeoutAt = Date.now() + this.timeoutWarningMs;

    const result = await Swal.fire({
      html: this.timeoutHtml(this.minutesRemaining(timeoutAt)),
      showCancelButton: true,
      confirmButtonText: 'Continue Session',
      cancelButtonText: 'Sign Out',
      reverseButtons: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      buttonsStyling: false,
      timer: this.timeoutWarningMs,
      timerProgressBar: true,
      customClass: {
        popup: 'apex-swal-popup apex-session-timeout-popup',
        htmlContainer: 'apex-swal-text apex-session-timeout-text',
        actions: 'apex-swal-actions apex-session-timeout-actions',
        confirmButton: 'apex-swal-confirm apex-session-timeout-confirm',
        cancelButton: 'apex-swal-cancel apex-session-timeout-cancel',
        timerProgressBar: 'apex-session-timeout-progress',
      },
      didOpen: () => {
        this.startCountdown(timeoutAt);
      },
      willClose: () => {
        this.clearCountdownTimer();
      },
    });

    this.warningOpen = false;

    if (result.isConfirmed) {
      await this.continueSession();
      return;
    }

    await this.signOut();
  }

  private startCountdown(timeoutAt: number): void {
    this.clearCountdownTimer();
    this.countdownTimer = setInterval(() => {
      const countdown = Swal.getHtmlContainer()?.querySelector<HTMLElement>('[data-session-countdown]');
      if (countdown) {
        countdown.textContent = this.minutesRemaining(timeoutAt);
      }
    }, 1000);
  }

  private async continueSession(): Promise<void> {
    this.lastActivityAt = Date.now();
    await firstValueFrom(this.sessionRefresh.refresh()).catch(() => undefined);
    this.touchSession();
    this.scheduleWarning();
  }

  private async signOut(): Promise<void> {
    this.clearTimers();
    await this.auth.logout().catch(() => undefined);
    window.location.href = `${environment.publicAppUrl}/login?returnUrl=${encodeURIComponent(this.currentReturnPath())}`;
  }

  private touchSession(): void {
    this.http.post(`${environment.apiGatewayUrl}/api/v1/sessions/touch`, {}, {
      context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
      withCredentials: true,
    }).subscribe({
      next: () => undefined,
      error: () => undefined,
    });
  }

  private timeoutHtml(minutesLabel: string): string {
    return `
      <div class="apex-session-timeout-visual" aria-hidden="true">
        <span class="apex-session-timeout-bubble apex-session-timeout-bubble--one"></span>
        <span class="apex-session-timeout-bubble apex-session-timeout-bubble--two"></span>
        <span class="apex-session-timeout-bubble apex-session-timeout-bubble--three"></span>
        <span class="apex-session-timeout-clock">
          <span class="apex-session-timeout-clock-hand apex-session-timeout-clock-hand--hour"></span>
          <span class="apex-session-timeout-clock-hand apex-session-timeout-clock-hand--minute"></span>
        </span>
      </div>
      <h2>Session Timeout</h2>
      <p>Due to inactivity, your session will time out in <strong data-session-countdown>${minutesLabel}</strong>.</p>
    `;
  }

  private minutesRemaining(timeoutAt: number): string {
    const seconds = Math.max(Math.ceil((timeoutAt - Date.now()) / 1000), 0);
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  private verifyAndStartSessionWork(): void {
    this.clearSessionVerifyRetryTimer();
    this.verifySession().subscribe({
      next: (authenticated) => {
        if (authenticated) {
          this.startSessionWork();
        } else {
          this.scheduleSessionVerificationRetry();
        }
      },
      error: () => this.scheduleSessionVerificationRetry(),
    });
  }

  private verifySession(): Observable<boolean> {
    const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    const verifyRequest = () => this.http.get<{ authenticated?: boolean }>(`${environment.apiGatewayUrl}/api/v1/user/auth-verify`, {
      context,
      withCredentials: true,
    });

    return verifyRequest().pipe(
      timeout(8000),
      switchMap((session) => of(session?.authenticated === true)),
      catchError(() => this.sessionRefresh.refresh().pipe(
        switchMap(() => timer(this.cookieSettleDelayMs)),
        switchMap(() => verifyRequest()),
        switchMap((session) => of(session?.authenticated === true)),
        catchError(() => of(false)),
      )),
    );
  }

  private scheduleSessionVerificationRetry(): void {
    if (!this.started || this.sessionWorkStarted || typeof window === 'undefined' || this.isLoginRoute()) {
      return;
    }

    this.clearSessionVerifyRetryTimer();
    this.sessionVerifyRetryTimer = setTimeout(() => {
      this.verifyAndStartSessionWork();
    }, this.sessionVerifyRetryMs);
  }

  private isLoginRoute(): boolean {
    return typeof window !== 'undefined' && window.location.pathname.startsWith('/login');
  }

  private currentReturnPath(): string {
    if (typeof window === 'undefined') {
      return '/dashboard';
    }

    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return path && path !== '/' ? path : '/dashboard';
  }

  private clearTimers(): void {
    this.clearWarningTimer();
    this.clearCountdownTimer();
    this.clearTouchTimer();
    this.clearRefreshTimer();
    this.clearSessionVerifyRetryTimer();
  }

  private clearWarningTimer(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private clearCountdownTimer(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private clearTouchTimer(): void {
    if (this.touchTimer) {
      clearInterval(this.touchTimer);
      this.touchTimer = null;
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private clearSessionVerifyRetryTimer(): void {
    if (this.sessionVerifyRetryTimer) {
      clearTimeout(this.sessionVerifyRetryTimer);
      this.sessionVerifyRetryTimer = null;
    }
  }
}
